/**
 * Integration Slot Component
 * 
 * Renders integration hooks for a specific UI slot dynamically.
 * Integrations define which slots they want to appear in, and this
 * component renders the appropriate UI for each enabled integration.
 * 
 * @created 2025-12-26
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useSlotHooks, useIntegrationHooks, IntegrationHook } from '@/contexts/IntegrationHooksContext';
import { Button } from '@/components/ui/button';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Loader2, Search, FileText, Download } from 'lucide-react';
import { toast } from 'sonner';

// Icon mapping for integration icons
const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  confluence: FileText,
  document: FileText,
  search: Search,
  download: Download,
};

function getIcon(iconName?: string) {
  if (!iconName) return FileText;
  return ICON_MAP[iconName.toLowerCase()] || FileText;
}

interface IntegrationSlotProps {
  slot: string;
  variant?: 'button' | 'dropdown' | 'inline';
  buttonVariant?: 'default' | 'outline' | 'ghost' | 'secondary';
  buttonSize?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
  disabled?: boolean;
  onActionComplete?: (integrationId: string, result: unknown) => void;
}

/**
 * Generic Integration Slot that renders buttons/actions for all integrations
 * that have registered hooks for the given slot.
 */
export function IntegrationSlot({
  slot,
  variant = 'button',
  buttonVariant = 'outline',
  buttonSize = 'sm',
  className,
  disabled,
  onActionComplete,
}: IntegrationSlotProps) {
  const { hooks, hasHooks, isLoading } = useSlotHooks(slot);

  if (isLoading || !hasHooks) {
    return null;
  }

  return (
    <>
      {hooks.map((hook) => (
        <IntegrationHookButton
          key={`${hook.integrationId}-${hook.slot}-${hook.action}`}
          hook={hook}
          variant={buttonVariant}
          size={buttonSize}
          className={className}
          disabled={disabled}
          onActionComplete={onActionComplete}
        />
      ))}
    </>
  );
}

interface IntegrationHookButtonProps {
  hook: IntegrationHook;
  variant?: 'default' | 'outline' | 'ghost' | 'secondary';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
  disabled?: boolean;
  onActionComplete?: (integrationId: string, result: unknown) => void;
}

/**
 * Individual button for an integration hook.
 * Opens a modal appropriate for the action type.
 */
function IntegrationHookButton({
  hook,
  variant = 'outline',
  size = 'sm',
  className,
  disabled,
  onActionComplete,
}: IntegrationHookButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const Icon = getIcon(hook.icon);

  return (
    <>
      <Button
        variant={variant}
        size={size}
        className={className}
        disabled={disabled}
        onClick={() => setIsOpen(true)}
      >
        <Icon className="h-4 w-4 mr-2" />
        <span className="hidden sm:inline">{hook.label}</span>
        <span className="sm:hidden">{hook.integrationName}</span>
      </Button>

      <IntegrationActionModal
        hook={hook}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onComplete={(result) => {
          onActionComplete?.(hook.integrationId, result);
          setIsOpen(false);
        }}
      />
    </>
  );
}

interface IntegrationActionModalProps {
  hook: IntegrationHook;
  isOpen: boolean;
  onClose: () => void;
  onComplete?: (result: unknown) => void;
}

/**
 * Modal for executing integration actions.
 * Handles different action types (search, export, etc.)
 */
function IntegrationActionModal({
  hook,
  isOpen,
  onClose,
  onComplete,
}: IntegrationActionModalProps) {
  const { invokeAction } = useIntegrationHooks();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);

  const handleSearch = useCallback(async () => {
    console.log('[IntegrationActionModal] Searching for:', searchQuery);
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    try {
      console.log('[IntegrationActionModal] Invoking action:', hook.integrationId, 'search');
      const result = await invokeAction<{ results: any[] }>(
        hook.integrationId,
        'search',
        { query: searchQuery, limit: 20 }
      );
      console.log('[IntegrationActionModal] Search result:', result);

      if (result.success && result.result?.results) {
        setSearchResults(result.result.results);
      } else {
        toast.error(result.error || 'Search failed');
        setSearchResults([]);
      }
    } catch (error: any) {
      toast.error('Search failed');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [hook.integrationId, searchQuery, invokeAction]);

  // Load initial results when modal opens
  useEffect(() => {
    if (isOpen && !searchResults.length) {
      const loadInitialResults = async () => {
        setIsSearching(true);
        try {
          console.log('[IntegrationActionModal] Loading initial results');
          const result = await invokeAction<{ results: any[] }>(
            hook.integrationId,
            'search',
            { query: '', limit: 20 } // Empty query to get recent pages
          );
          console.log('[IntegrationActionModal] Initial results:', result);

          if (result.success && result.result?.results) {
            setSearchResults(result.result.results);
          }
        } catch (error: any) {
          console.error('[IntegrationActionModal] Failed to load initial results:', error);
        } finally {
          setIsSearching(false);
        }
      };

      loadInitialResults();
    }
  }, [isOpen, hook.integrationId, invokeAction, searchResults.length]);

  const handleExport = useCallback(async (item: any) => {
    setIsExporting(true);
    setSelectedItem(item);

    try {
      const result = await invokeAction<{
        file: string;
        fileName: string;
        mimeType: string;
      }>(hook.integrationId, 'export', {
        pageId: item.id,
        format: 'pdf',
      });

      if (result.success && result.result) {
        // Convert base64 to file and trigger download or pass to callback
        const { file, fileName, mimeType } = result.result;
        
        // Create blob from base64
        const byteCharacters = atob(file);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });
        const fileObject = new File([blob], fileName, { type: mimeType });

        toast.success(`Exported "${item.title || fileName}" successfully`);
        onComplete?.({ file: fileObject, item, result: result.result });
      } else {
        toast.error(result.error || 'Export failed');
      }
    } catch (error: any) {
      toast.error(`Export failed: ${error.message}`);
    } finally {
      setIsExporting(false);
      setSelectedItem(null);
    }
  }, [hook.integrationId, invokeAction, onComplete]);

  const handleClose = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSelectedItem(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col bg-card">
        <DialogHeader>
          <DialogTitle>{hook.label}</DialogTitle>
          <DialogDescription>
            {hook.description || `${hook.action} via ${hook.integrationName}`}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 flex flex-col gap-4 overflow-hidden">
          {/* Search Bar */}
          <div className="flex gap-2">
            <div className="flex-1">
              <Input
                placeholder={`Search ${hook.integrationName}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Button
              onClick={handleSearch}
              disabled={isSearching || !searchQuery.trim()}
            >
              {isSearching ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Results */}
          <div className="border rounded-md flex-1 overflow-auto min-h-[200px]">
            {isSearching ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <span className="ml-2">Searching...</span>
              </div>
            ) : searchResults.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
                <FileText className="h-8 w-8 mb-2" />
                <p>{searchQuery ? 'No results found' : `Search ${hook.integrationName}`}</p>
              </div>
            ) : (
              <div className="divide-y">
                {searchResults.map((item) => (
                  <div
                    key={item.id}
                    className="p-3 hover:bg-accent cursor-pointer flex items-center justify-between"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.title}</p>
                      <p className="text-xs text-muted-foreground">
                        {item.spaceName || item.spaceKey || ''}
                        {item.lastModified && ` • ${new Date(item.lastModified).toLocaleDateString()}`}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={isExporting && selectedItem?.id === item.id}
                      onClick={() => handleExport(item)}
                    >
                      {isExporting && selectedItem?.id === item.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Import'
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default IntegrationSlot;
